/**
 * Lazily loads the Google Maps JavaScript API (Places library) exactly once.
 *
 * The key comes from VITE_GOOGLE_MAPS_API_KEY. When no key is configured the
 * loader resolves to `null` so callers fall back to manual coordinate entry —
 * the delivery flow keeps working without any Google dependency.
 */
let loaderPromise: Promise<any | null> | null = null;

export function getGoogleMapsApiKey(): string {
  return (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
}

export function loadGoogleMaps(): Promise<any | null> {
  if (loaderPromise) {
    return loaderPromise;
  }

  const key = getGoogleMapsApiKey();
  if (!key) {
    loaderPromise = Promise.resolve(null);
    return loaderPromise;
  }

  loaderPromise = new Promise((resolve) => {
    const w = window as any;
    if (w.google?.maps?.places) {
      resolve(w.google);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve((window as any).google ?? null);
    script.onerror = () => resolve(null); // fail soft → manual entry still works
    document.head.appendChild(script);
  });

  return loaderPromise;
}
